import { useState, useEffect, useCallback } from 'react';

/**
 * Percent width/left for a faux horizontal scrollbar thumb (mobile browsers hide native bars).
 */
export function useHorizontalScrollThumb(scrollerRef) {
  const [thumbStyle, setThumbStyle] = useState({ width: '100%', left: '0%' });

  const update = useCallback(() => {
    const el = scrollerRef?.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) {
      setThumbStyle({ width: '100%', left: '0%' });
      return;
    }
    const thumbW = Math.max(14, (clientWidth / scrollWidth) * 100);
    const maxLeft = 100 - thumbW;
    const left = (scrollLeft / maxScroll) * maxLeft;
    setThumbStyle({ width: `${thumbW}%`, left: `${left}%` });
  }, [scrollerRef]);

  useEffect(() => {
    const el = scrollerRef?.current;
    update();
    if (!el) return undefined;

    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        update();
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', update);
      if (ro) ro.disconnect();
    };
  }, [scrollerRef, update]);

  return thumbStyle;
}
